import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Hook to manage shipment selection (single, multi-select, long-press)
 * @param {Function} getTrackCarsFor - Function to get cars for a track
 * @param {Function} openShipment - Function to open a shipment
 * @param {Array} externalSelectedShipmentIds - Optional external state for selectedShipmentIds
 * @param {Function} externalSetSelectedShipmentIds - Optional external setter for selectedShipmentIds
 * @param {Object} externalLastSelectedContext - Optional external state for lastSelectedContext
 * @param {Function} externalSetLastSelectedContext - Optional external setter for lastSelectedContext
 * @param {Function} canOpenShipment - Predicate to determine whether a shipment can be opened.
 * @returns {Object} Selection state and handlers
 */
export const useTrackLocationSelection = (
  getTrackCarsFor,
  openShipment,
  externalSelectedShipmentIds,
  externalSetSelectedShipmentIds,
  externalLastSelectedContext,
  externalSetLastSelectedContext,
  canOpenShipment
) => {
  const [internalSelectedShipmentIds, setInternalSelectedShipmentIds] = useState([]);
  const [internalLastSelectedContext, setInternalLastSelectedContext] = useState(null);
  
  // Use external state if provided, otherwise use internal state
  const selectedShipmentIds = externalSelectedShipmentIds !== undefined ? externalSelectedShipmentIds : internalSelectedShipmentIds;
  const setSelectedShipmentIds = externalSetSelectedShipmentIds || setInternalSelectedShipmentIds;
  const lastSelectedContext = externalLastSelectedContext !== undefined ? externalLastSelectedContext : internalLastSelectedContext;
  const setLastSelectedContext = externalSetLastSelectedContext || setInternalLastSelectedContext;
  
  const selectedShipmentIdsRef = useRef(selectedShipmentIds);
  const lastSelectedContextRef = useRef(lastSelectedContext);
  
  // Sync refs
  useEffect(() => {
    selectedShipmentIdsRef.current = selectedShipmentIds;
  }, [selectedShipmentIds]);

  useEffect(() => {
    lastSelectedContextRef.current = lastSelectedContext;
  }, [lastSelectedContext]);
  
  const clearSelection = useCallback(() => {
    setSelectedShipmentIds([]);
    setLastSelectedContext(null);
  }, [setSelectedShipmentIds, setLastSelectedContext]);
  
  const handleShipmentSelect = useCallback(
    (event, shipment, trackLocationId, options = {}) => {
      const { isLongPress = false } = options;
      const id = shipment.id;
      const currentSelected = selectedShipmentIdsRef.current || [];
      const isMeta = event?.metaKey || event?.ctrlKey;
      const isShift = event?.shiftKey;

      // Use visible index for selection logic to maintain consistent UX during filtration
      const carsOnTrack = getTrackCarsFor(trackLocationId);
      const visibleIdx = carsOnTrack.findIndex((c) => c.id === id);

      if (isLongPress) {
        // STAFF LEVEL OPTIMIZATION: Disable cross-track selection
        const sameTrack = 
          !lastSelectedContextRef.current || 
          lastSelectedContextRef.current.trackLocationId === trackLocationId;

        if (!sameTrack) {
          setSelectedShipmentIds([id]);
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
          return;
        }

        const isAlreadySelected = currentSelected.includes(id);
        const next = isAlreadySelected
          ? currentSelected.filter((shipmentId) => shipmentId !== id)
          : [...currentSelected, id];
        setSelectedShipmentIds(next);
        setLastSelectedContext({ trackLocationId, index: visibleIdx });
        return;
      }

      if (
        isShift &&
        lastSelectedContextRef.current
      ) {
        if (lastSelectedContextRef.current.trackLocationId !== trackLocationId) {
          // STAFF LEVEL OPTIMIZATION: Disable cross-track selection
          setSelectedShipmentIds([id]);
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
          return;
        }

        const start = Math.min(lastSelectedContextRef.current.index, visibleIdx);
        const end = Math.max(lastSelectedContextRef.current.index, visibleIdx);
        const idsInRange = carsOnTrack.slice(start, end + 1).map((c) => c.id);
        setSelectedShipmentIds(idsInRange);
        return;
      }

      if (isMeta) {
        // STAFF LEVEL OPTIMIZATION: Disable cross-track selection
        const sameTrack = 
          !lastSelectedContextRef.current || 
          lastSelectedContextRef.current.trackLocationId === trackLocationId;

        if (!sameTrack) {
          setSelectedShipmentIds([id]);
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
          return;
        }

        const isAlreadySelected = currentSelected.includes(id);
        const next = isAlreadySelected
          ? currentSelected.filter((shipmentId) => shipmentId !== id)
          : [...currentSelected, id];
        setSelectedShipmentIds(next);
        setLastSelectedContext({ trackLocationId, index: visibleIdx });
        return;
      }

      // Default: simple tap/click opens when openable; otherwise single-select.
      if (!canOpenShipment(shipment)) {
        setSelectedShipmentIds([id]);
        setLastSelectedContext({ trackLocationId, index: visibleIdx });
        return;
      }
      clearSelection();
      openShipment(shipment);
    },
    [
      getTrackCarsFor,
      openShipment,
      clearSelection,
      setSelectedShipmentIds,
      setLastSelectedContext,
      canOpenShipment,
    ]
  );

  const handleShipmentLongPress = useCallback(
    (shipment, trackLocationId) => {
      handleShipmentSelect(null, shipment, trackLocationId, {
        isLongPress: true,
      });
    },
    [handleShipmentSelect]
  );

  return {
    selectedShipmentIds,
    setSelectedShipmentIds,
    lastSelectedContext,
    setLastSelectedContext,
    handleShipmentSelect,
    handleShipmentLongPress,
  };
};

